export type Season = 'summer' | 'winter';
export type CourseMeetingStatus = '进行中' | '已结束' | '未开始';

const winterTimes = ['08:00 – 08:50', '09:00 – 09:50', '10:10 – 11:00', '11:10 – 12:00', '14:00 – 14:50', '15:00 – 15:50', '16:10 – 17:00', '17:10 – 18:00', '19:10 – 20:00', '20:10 – 21:00', '21:10 – 22:00'];
const summerTimes = ['08:00 – 08:50', '09:00 – 09:50', '10:10 – 11:00', '11:10 – 12:00', '14:30 – 15:20', '15:30 – 16:20', '16:40 – 17:30', '17:40 – 18:30', '19:40 – 20:30', '20:40 – 21:30', '21:40 – 22:30'];

export function sectionTimes(season: Season) {
  return (season === 'summer' ? summerTimes : winterTimes).map((time, index) => ({ section: index + 1, time }));
}

export function seasonForWeek(requestedWeek: number): Season {
  return seasonForDate(weekRange(requestedWeek).start);
}

// The timetable changes by week: every day in a week uses its Monday's clock.
export function seasonForDate(localDate: string): Season {
  const [year, month, day] = localDate.slice(0, 10).split('-').map(Number);
  const monday = new Date(Date.UTC(year, month - 1, day));
  monday.setUTCDate(monday.getUTCDate() - (monday.getUTCDay() + 6) % 7);
  const monthDay = monday.toISOString().slice(5, 10);
  return monthDay >= '05-01' && monthDay < '10-01' ? 'summer' : 'winter';
}

export function weekRange(requestedWeek: number) {
  const week = Math.max(1, Math.min(16, requestedWeek));
  const start = new Date(Date.UTC(2026, 8, 14 + (week - 1) * 7));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const format = (date: Date) => date.toISOString().slice(0, 10);
  return { start: format(start), end: format(end) };
}

export function currentTermWeek() {
  return currentTermWeekAt(new Date());
}

function shanghaiDate(now: Date) {
  return new Date(now.getTime() + 8 * 60 * 60 * 1000);
}

export function currentTermWeekAt(now: Date) {
  const chinaNow = shanghaiDate(now);
  const today = Date.UTC(chinaNow.getUTCFullYear(), chinaNow.getUTCMonth(), chinaNow.getUTCDate());
  const termStart = Date.UTC(2026, 8, 14);
  return Math.max(1, Math.min(16, Math.floor((today - termStart) / 86_400_000 / 7) + 1));
}

export function currentWeekdayInShanghai() {
  return currentWeekdayInShanghaiAt(new Date());
}

export function currentWeekdayInShanghaiAt(now: Date) {
  return (shanghaiDate(now).getUTCDay() + 6) % 7;
}

export function isMeetingInWeek(weeks: string | undefined, week: number) {
  if (!weeks?.trim()) return true;
  const value = weeks.replaceAll(' ', '').replaceAll('，', ',').replaceAll('、', ',');
  const ranges = [...value.matchAll(/(\d+)\s*(?:-|~|至)\s*(\d+)/g)].map((match) => [Number(match[1]), Number(match[2])] as const);
  const singles = [...value.matchAll(/(?<![-~至])\b(\d+)\b(?!\s*(?:-|~|至))/g)].map((match) => Number(match[1]));
  const included = ranges.length || singles.length
    ? ranges.some(([start, end]) => week >= Math.min(start, end) && week <= Math.max(start, end)) || singles.includes(week)
    : true;
  if (!included) return false;
  if (/(?:单周|单)/.test(value)) return week % 2 === 1;
  if (/(?:双周|双)/.test(value)) return week % 2 === 0;
  return true;
}

function toMinutes(time: string | undefined) {
  const match = time?.match(/(\d{1,2}):(\d{2})/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
}

function endMinutes(time: string | undefined) {
  const matches = [...(time?.matchAll(/(\d{1,2}):(\d{2})/g) ?? [])];
  const match = matches.at(-1);
  return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
}

export function courseMeetingStatus({ selectedWeek, day, slot, duration = 1, now = new Date() }: { selectedWeek: number; day: number; slot: number; duration?: number; now?: Date }): CourseMeetingStatus {
  const currentWeek = currentTermWeekAt(now);
  if (selectedWeek < currentWeek) return '已结束';
  if (selectedWeek > currentWeek) return '未开始';
  const currentDay = currentWeekdayInShanghaiAt(now);
  if (day < currentDay) return '已结束';
  if (day > currentDay) return '未开始';
  const times = sectionTimes(seasonForWeek(selectedWeek));
  const start = toMinutes(times.find((item) => item.section === slot)?.time);
  const end = endMinutes(times.find((item) => item.section === Math.min(times.length, slot + Math.max(1, duration) - 1))?.time);
  if (start === undefined || end === undefined) return '未开始';
  const chinaNow = shanghaiDate(now);
  const currentMinutes = chinaNow.getUTCHours() * 60 + chinaNow.getUTCMinutes();
  if (currentMinutes < start) return '未开始';
  return currentMinutes <= end ? '进行中' : '已结束';
}
